import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CHART_TRAFFIC_INK, statText } from '@knitknot/shared/components/report';
import { cn } from '@knitknot/shared/utils';
import { Sparkline } from '@knitknot/shared/components/ui/sparkline';
import { deltaGlyphClass } from '@knitknot/shared/components/ui/delta-indicator';
import { SECTION_ACTION_CLASS, SectionHeader } from '@knitknot/shared/components/ui/section-header';
import { useWorkspaceStore } from '@/hooks/state';
import { useTrafficSummary } from '@/hooks/api/useTrafficSummary';
import { useCloudflareCapabilities } from '@/hooks/api/useCloudflareCapabilities';
import { aiVisitsModuleState, crawlersSparkline, visitsSparkline } from '@/lib/aiVisits';

/* ────────────────────────────────────────────────────────────────────────────
   AiVisitsModule — /home's ONE AI-traffic signal (design §2 D2/D5, extended by
   the Site hub plan).

   Always rendered, in the same bare-section register as the two rails beneath
   it (no card, no tiles, no icons): the TrendHero above it stays benchmark-only,
   and people who arrived from an AI answer get their own stable slot so the
   fact is on the dashboard from the first sync rather than after two benchmark
   weeks.

   Three lines, in descending certainty:
     1. AI-referred visits — the headline number + its sentence + a 90-day
        sparkline. Four states; the provider is printed in every connected one,
        because GA4 and Cloudflare count different things and are never merged.
        Colour lands on exactly one token (the delta) and only when it is news.
     2. crawler fetches — the muted line, with its own sparkline.
     3. the adaptive SITE sentence — the hub's one entry point from Home, honest
        for a workspace with no integration and no beta.

   Two windows, deliberately: the headline is a 7-day count against the prior 7
   (a week is the unit a reader acts on), while the sparklines want 90 days of
   shape. Both reads are server-cached per (workspace, days), and the 90-day one
   is only issued once a lane exists to draw.

   Sparklines omit themselves under two measured points and leave entirely
   under 720px (the `chart` breakpoint) — the number and the sentence carry the
   fact and still link through.
   ──────────────────────────────────────────────────────────────────────────── */

const VISITS_HREF = '/traffic/referrals';
const CRAWLERS_HREF = '/traffic/agents';
const INTEGRATIONS_HREF = '/settings/integrations';

/** The sparkline window. 7 days of shape is a scribble; 90 is a trend. */
const SPARKLINE_DAYS = 90;

const INLINE_LINK = 'focus-ring rounded text-foreground underline-offset-[3px] hover:underline';

export function AiVisitsModule() {
  const { workspaceId } = useWorkspaceStore();
  const cloudflare = useCloudflareCapabilities(workspaceId ?? undefined);
  const summaryQ = useTrafficSummary(7);
  const summary = summaryQ.data;

  // The shape read, issued only once there is a lane to draw. A workspace with
  // no analytics and no Cloudflare never pays for it.
  const hasLane = !!(summary?.visits || summary?.crawlers);
  const trendQ = useTrafficSummary(SPARKLINE_DAYS, hasLane);

  // A cold read must not assert an absence (rule 59): hold the slot's height
  // with nothing in it rather than flashing "Connect Google Analytics" at a
  // workspace that is connected.
  if (summaryQ.isLoading) return <section aria-busy="true" className="min-h-8" />;
  // The endpoint answers null lanes, never errors; a failed request still
  // must not read as "not connected", so the slot stays quiet.
  if (summaryQ.isError) return null;

  const state = aiVisitsModuleState(summary?.visits);
  const crawlers = summary?.crawlers;
  const trafficHref = state.kind !== 'not-connected' ? VISITS_HREF : crawlers ? CRAWLERS_HREF : INTEGRATIONS_HREF;
  const cloudflareNeedsZones = cloudflare.installation?.analytics_state === 'account_selected';
  const verifiedFetches =
    crawlers?.verified_share == null ? null : Math.round(crawlers.fetches * crawlers.verified_share);
  const trend = trendQ.data;
  const visitsSpark = visitsSparkline(trend?.visits, SPARKLINE_DAYS);
  const crawlersSpark = crawlersSparkline(trend?.crawlers, SPARKLINE_DAYS);

  return (
    <section aria-label="Observed reach" className="border-y border-border bg-card">
      <div className="px-5 py-4 sm:px-6">
        <div>
          <SectionHeader
            title="Observed reach"
            size="lg"
            action={
              <Link to={trafficHref} className={SECTION_ACTION_CLASS} aria-label="View traffic">
                Traffic
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            }
          />
          <p className="mt-1 max-w-2xl text-sm leading-5 text-muted-foreground">
            Requests and referred visits from connected analytics providers. Each source stays separate.
          </p>
        </div>
      </div>

      <div className="grid border-t border-border md:grid-cols-2">
        <div className="min-w-0 border-l-4 border-secondary px-5 py-5 sm:px-6">
          <p className="text-detail font-semibold uppercase tracking-[0.1em] text-secondary">Verified agent fetches</p>
          {crawlers?.first_sync_pending ? (
            <p className="mt-1 text-sm text-muted-foreground">First Cloudflare sync is in progress.</p>
          ) : crawlers ? (
            <>
              <div className="mt-2 flex min-w-0 items-end gap-3">
                <Link
                  to={CRAWLERS_HREF}
                  className={cn(
                    statText.value,
                    'focus-ring rounded text-3xl font-semibold tracking-tight no-underline hover:underline',
                  )}
                >
                  {verifiedFetches?.toLocaleString()}
                </Link>
                {crawlersSpark && <Sparkline points={crawlersSpark.points} label={crawlersSpark.label} />}
              </div>
              <p className="mt-2 text-xs leading-5 text-muted-foreground">
                {verifiedFetches == null
                  ? `${crawlers.fetches.toLocaleString()} observed crawler fetches; identity is not verified.`
                  : `of ${crawlers.fetches.toLocaleString()} observed crawler fetches in the last 7 days`}
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              <Link
                to={cloudflareNeedsZones ? '/settings/integrations/cloudflare' : INTEGRATIONS_HREF}
                className={INLINE_LINK}
              >
                {cloudflareNeedsZones ? 'Choose Cloudflare zones' : 'Connect Cloudflare'}
              </Link>{' '}
              {cloudflareNeedsZones
                ? 'to begin importing verified agent requests.'
                : 'to verify agent requests at the edge.'}
            </p>
          )}
        </div>

        <div className="min-w-0 border-t border-border px-5 py-5 sm:px-6 md:border-l md:border-t-0">
          <p className="text-detail font-semibold uppercase tracking-[0.1em] text-ink-supporting">AI-referred visits</p>
          {state.kind === 'not-connected' && (
            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              <Link to={INTEGRATIONS_HREF} className={INLINE_LINK}>
                Connect analytics
              </Link>{' '}
              to measure visits from AI answers.
            </p>
          )}
          {state.kind === 'needs-reconnect' && (
            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              <Link to={INTEGRATIONS_HREF} className={INLINE_LINK}>
                {state.provider} needs reconnecting
              </Link>
              .
            </p>
          )}
          {state.kind === 'first-sync' && (
            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              {state.provider === 'Cloudflare'
                ? (cloudflare.history.message ?? 'Importing available history now')
                : 'Importing recent data now'}{' '}
              · via {state.provider}
            </p>
          )}
          {state.kind === 'data' && (
            <>
              <div className="mt-2 flex min-w-0 items-end gap-3">
                <Link
                  to={VISITS_HREF}
                  className={cn(
                    statText.value,
                    'focus-ring rounded text-3xl font-semibold tracking-tight no-underline hover:underline',
                  )}
                >
                  {state.headline}
                </Link>
                {visitsSpark && (
                  <Sparkline points={visitsSpark.points} label={visitsSpark.label} color={CHART_TRAFFIC_INK} />
                )}
              </div>
              <p className="mt-2 text-xs leading-5 text-muted-foreground">
                {state.metaBefore}
                {state.delta && (
                  <>
                    {' · '}
                    <span
                      className={cn(
                        'tabular-nums',
                        state.deltaEmphasised && deltaGlyphClass(state.delta.startsWith('+') ? 'up' : 'down'),
                      )}
                    >
                      {state.delta}
                    </span>
                  </>
                )}
                {' · '}
                {state.metaAfter}
                {state.sentence ? ` · ${state.sentence}` : ''}
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default AiVisitsModule;
